import React, { FC } from 'react';
import { useQuery } from 'react-apollo';
import { useParams } from 'react-router';
import { Divider, Table } from 'semantic-ui-react';
import moment from 'moment';

import DefaultLayout from 'layouts/DefaultLayout';
import ErrorDisplay from 'components/ErrorDisplay';
import Loader from 'components/Loader';

import FETCH_ORDER_SUMMARY from 'graphql/queries/fetchOrderSummary.graphql';

import theme from './theme.scss';

type OrderQueryResponse = {
  order: {
    id: string;
    referenceId: string;
    status: string;
    createdAt: string;
    orderedBy: {
      fullName: string;
    };
    notes: string;
    dishes: {
      id: string;
      name: string;
      quantity: number;
      price: number;
    }[];
    grossPrice: number;
    taxes: number;
    totalPrice: number;
    vendor: {
      name: string;
    };
  };
};

type OrderQueryVariables = {
  orderId: string;
};

const OrderSummary: FC = () => {
  const { orderId } = useParams();

  const { loading, error, data } = useQuery<OrderQueryResponse, OrderQueryVariables>(
    FETCH_ORDER_SUMMARY,
    {
      variables: {
        orderId: orderId ? orderId : '',
      },
    }
  );

  if (loading) return <Loader type="screen" />;

  if (error || !data || !data.order) return <ErrorDisplay type="screen" />;

  const order = data.order;

  // const address = {
  //   line1: 'Hyderabad Biryani',
  //   line2: '',
  //   city: '',
  //   state: '',
  //   pincode: '',
  // };

  return (
    <DefaultLayout>
      <div className={theme.orderSummary}>
        <div className={theme.content}>
          <h1>
            Your order from <br />
            <span className={theme.organizationName}>
              {order.vendor ? order.vendor.name : ''}
            </span>{' '}
            on <br />
            <span className={theme.visitTime}>
              {moment(order.createdAt).format('DD MMM YYYY')}.
            </span>
          </h1>
          <Divider />
          <h2>Details</h2>
          <div className={theme.information}>
            <div className={theme.field}>
              <p className={theme.label}>ORDER NUMBER</p>
              <p className={theme.value}>{order.referenceId}</p>
            </div>
            <div className={theme.field}>
              <p className={theme.label}>ORDER TIME</p>
              <p className={theme.value}>
                {moment(order.createdAt).format('HH:mm a, DD MMMM YYYY')}
              </p>
            </div>
            <div className={theme.field}>
              <p className={theme.label}>ORDERED FROM</p>
              <p className={theme.value}>{order.vendor ? order.vendor.name : ''}</p>
              {/* <p
                className={theme.value}
              >{`${address.line1} ${address.line2}, ${address.city}, ${address.state}, - ${address.pincode}`}</p> */}
            </div>
            <div className={theme.field}>
              <p className={theme.label}>STATUS</p>
              <p className={theme.value}>{order.status}</p>
            </div>
            <div className={theme.field}>
              <p className={theme.label}>NAME</p>
              <p className={theme.value}>{order.orderedBy.fullName}</p>
            </div>
            <div className={theme.field}>
              <p className={theme.label}>NOTES</p>
              <p className={theme.value}>{order.notes}</p>
            </div>
          </div>
          <Divider />
          <h2>Purchase</h2>
          <Table unstackable compact className={theme.itemTable}>
            <Table.Header className={theme.itemHeader}>
              <Table.Row>
                <Table.HeaderCell width="10">ITEM NAME</Table.HeaderCell>
                <Table.HeaderCell width="3" textAlign="center">
                  QUANTITY
                </Table.HeaderCell>
                <Table.HeaderCell width="3" textAlign="center">
                  UNIT PRICE
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body className={theme.itemBody}>
              {order.dishes.map(dish => (
                <Table.Row key={dish.id} className={theme.itemRow}>
                  <Table.Cell width="10">{dish.name}</Table.Cell>
                  <Table.Cell width="3" textAlign="center">
                    {dish.quantity}
                  </Table.Cell>
                  <Table.Cell width="3" textAlign="center">
                    ₹ {dish.price}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
          <Table unstackable compact className={theme.purchaseSummary}>
            <Table.Body className={theme.itemBody}>
              <Table.Row className={theme.itemRow}>
                <Table.Cell width="10" />
                <Table.Cell width="3" textAlign="right">
                  Total:
                </Table.Cell>
                <Table.Cell width="3" textAlign="center">
                  ₹ {order.grossPrice}
                </Table.Cell>
              </Table.Row>
              <Table.Row className={theme.itemRow}>
                <Table.Cell width="10" />
                <Table.Cell width="3" textAlign="right">
                  Taxes:
                </Table.Cell>
                <Table.Cell width="3" textAlign="center">
                  ₹ {order.taxes}
                </Table.Cell>
              </Table.Row>
              <Table.Row className={theme.itemRow}>
                <Table.Cell width="10" />
                <Table.Cell width="3" textAlign="right">
                  Grand Total:
                </Table.Cell>
                <Table.Cell width="3" textAlign="center">
                  ₹ {order.totalPrice}
                </Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>
        </div>
      </div>
    </DefaultLayout>
  );
};

export default OrderSummary;
